import React, { useEffect } from 'react';
import { Shield, Lock, Eye, Users, Mail, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Privacy Policy - ContaX';
    
    // Track page view
    if (typeof window !== 'undefined' && (window as any).clarity) {
      (window as any).clarity('set', 'page', 'privacy-policy');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-blue-100 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to ContaX
          </button>
          
          <div className="flex items-center mb-4">
            <Shield className="w-12 h-12 mr-4" />
            <h1 className="text-4xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-xl text-blue-100">
            How we protect and handle your personal information
          </p>
          <p className="text-blue-200 mt-2">Last updated: December 2024</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Quick Summary */}
        <div className="bg-blue-50 rounded-xl p-6 mb-12">
          <h2 className="text-xl font-semibold text-blue-900 mb-4 flex items-center">
            <Eye className="w-6 h-6 mr-2" />
            Privacy at a Glance
          </h2>
          <div className="grid md:grid-cols-3 gap-6 text-sm">
            <div className="flex items-start">
              <Lock className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium text-blue-900">Data Protection</div>
                <div className="text-blue-700">We encrypt and secure all your data</div>
              </div>
            </div>
            <div className="flex items-start">
              <Users className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium text-blue-900">No Data Selling</div>
                <div className="text-blue-700">We never sell your personal information</div>
              </div>
            </div>
            <div className="flex items-start">
              <Mail className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium text-blue-900">Minimal Collection</div>
                <div className="text-blue-700">We only collect what's necessary</div>
              </div>
            </div>
          </div>
        </div>

        <div className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Information You Provide</h3>
            <ul className="space-y-2 text-gray-700">
              <li><strong>Contact Information:</strong> Email address, name (optional), and professional role (optional) when you join our waitlist</li>
              <li><strong>Communications:</strong> Messages you send us through contact forms or email</li>
              <li><strong>Feedback:</strong> Survey responses, feature requests, and product feedback</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Information We Automatically Collect</h3>
            <ul className="space-y-2 text-gray-700">
              <li><strong>Usage Data:</strong> How you interact with our website, pages visited, time spent</li>
              <li><strong>Technical Information:</strong> IP address, browser type, device information, operating system</li>
              <li><strong>Analytics Data:</strong> Website performance metrics and user behavior patterns (anonymized)</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
            <ul className="space-y-3 text-gray-700">
              <li><strong>Product Development:</strong> To notify you when ContaX is available and provide early access</li>
              <li><strong>Communication:</strong> To send you product updates, launch notifications, and respond to your inquiries</li>
              <li><strong>Improvement:</strong> To analyze usage patterns and improve our website and future product</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Information Sharing</h2>
            <p className="text-gray-700 mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in these limited circumstances:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li><strong>Service Providers:</strong> With trusted partners who help us operate our website (like Netlify for hosting and email services)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Security</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                We implement industry-standard security measures to protect your personal information:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li>• SSL/TLS encryption for data transmission</li>
                <li>• Secure hosting infrastructure with access controls</li>
                <li>• Regular security audits and updates</li>
                <li>• Limited access to personal data on a need-to-know basis</li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
            <p className="text-gray-700 mb-4">You have the following rights regarding your personal information:</p>
            <ul className="space-y-3 text-gray-700">
              <li><strong>Access:</strong> Request a copy of your personal data we hold</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data</li>
              <li><strong>Unsubscribe:</strong> Opt out of marketing communications at any time</li>
              <li><strong>Portability:</strong> Request your data in a portable format</li>
            </ul>
            <p className="text-gray-700 mt-4">
              To exercise these rights, contact us at <a href="mailto:privacy@contax.com" className="text-blue-600 hover:text-blue-800">privacy@contax.com</a>
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies and Analytics</h2>
            <p className="text-gray-700 mb-4">
              We use cookies and similar technologies to improve your experience:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li><strong>Essential Cookies:</strong> Necessary for website functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how you use our site (Microsoft Clarity, Cloudflare Analytics)</li>
              <li><strong>Performance Cookies:</strong> Monitor website performance and load times</li>
            </ul>
            <p className="text-gray-700 mt-4">
              You can control cookies through your browser settings, though disabling them may affect website functionality.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700">
              ContaX is not intended for children under 13. We do not knowingly collect personal information from children under 13. 
              If you are a parent and believe your child has provided us with personal information, please contact us immediately.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">International Transfers</h2>
            <p className="text-gray-700">
              Your information may be processed and stored in countries other than your own. We ensure appropriate safeguards 
              are in place to protect your data in accordance with this privacy policy.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this privacy policy from time to time. We will notify you of any material changes by email 
              or through our website. Your continued use of our services after changes constitute acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <div className="bg-blue-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                If you have questions about this privacy policy or our data practices, contact us:
              </p>
              <div className="space-y-2 text-gray-700">
                <div><strong>Email:</strong> <a href="mailto:privacy@contax.com" className="text-blue-600 hover:text-blue-800">privacy@contax.com</a></div>
                <div><strong>Subject Line:</strong> Privacy Policy Inquiry</div>
                <div><strong>Response Time:</strong> We'll respond within 72 hours</div>
              </div>
            </div>
          </section>
        </div>

        {/* Back to Homepage CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-8 text-center text-white mt-12">
          <h3 className="text-2xl font-bold mb-4">Ready to Transform Your Networking?</h3>
          <p className="text-blue-100 mb-6">Join thousands of professionals building stronger connections</p>
          <button
            onClick={() => navigate('/')}
            className="bg-white text-blue-600 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
          >
            Join the Waitlist
          </button>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;